import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/leadlancer-logo.png";

type SubmitState = "idle" | "loading" | "success" | "duplicate" | "error";

const ComingSoon = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<SubmitState>("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === "loading") return;

    setStatus("loading");

    const { error } = await supabase
      .from("launch_notifications")
      .insert({ email: trimmed });

    if (error) {
      const isDuplicate = error.code === "23505" || error.message?.includes("duplicate");
      setStatus(isDuplicate ? "duplicate" : "error");
      return;
    }

    setStatus("success");
  };

  const statusMessage = {
    success: "You're on the list!",
    duplicate: "You're already on the list.",
    error: "Something went wrong. Please try again.",
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col animate-fade-in">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[42rem] bg-primary/10 rounded-full blur-3xl" />

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20 text-center">
        <img
          src={logo}
          alt="Lead Lancer"
          className="h-10 md:h-12 w-auto mb-12 md:mb-16"
        />

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-3xl leading-[1.1]">
          Something New Is Coming
        </h1>

        <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
          A smarter way for service businesses to win more commercial contracts. Launching soon.
        </p>

        <div className="mt-10 md:mt-12 w-full max-w-md">
          {status === "success" || status === "duplicate" || status === "error" ? (
            <div className="flex items-center justify-center gap-3 text-base md:text-lg font-medium text-foreground">
              <span className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                <Check className="w-4 h-4 text-primary" />
              </span>
              {statusMessage[status]}
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
            >
              <label htmlFor="notify-email" className="sr-only">
                Email address
              </label>
              <Input
                id="notify-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 text-base bg-card/60 border-border/60"
              />
              <Button type="submit" variant="hero" size="lg" className="h-12 shrink-0" disabled={status === "loading"}>
                {status === "loading" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Notify Me
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 pb-10 text-center text-sm text-muted-foreground">
        <p>Lead Lancer Ltd</p>
      </footer>
    </div>
  );
};

export default ComingSoon;
